import { Result } from '@/types/base';
import request from './request';

interface Auth0MetaData {
    agencyId: string;
    mainAgencyId: string;
}

interface Accesses {
    antitumorLimitlessStartDate: boolean | null;
    lifePlusLimitlessStartDate: boolean | null;
    tplLimitlessStartDate: boolean | null;
    duplicateMotorPolicies: boolean | null;
    tplForceSigalAsInsurer: boolean | null;
    canBypassPrivateHealthAgeRestriction: boolean | null;
    canBypassPersonalAccidentsAgeRestriction: boolean | null;
    canBypassHealthTravelAgeRestriction: boolean | null;
    canBypassCascoProdYearRestriction: boolean | null;
    guaranteeCanSellBelowFloorValue: boolean | null;
    liabilityCanSellBelowFloorValue: boolean | null;
    canBypassAutososProdYearRestriction: boolean | null;
    canBypassMiniCascoProdYearRestriction: boolean | null;
    cascoCallSellBelowFloorValue: boolean | null;
    miniCascoCanSellOverLimitedEnginePower: boolean | null;
    marineCanSellBelowFloorValue: boolean | null;
    marineCargoCanSellBelowFloorValue: boolean | null;
}

interface User {
    identityName: string;
    displayName: string;
    auth0MetaData: Auth0MetaData;
    roles: string[];
    permissions: string[];
}

interface Agency {
    agency: {
        taxNumber: string;
        displayName: string;
        searchName: string;
        phoneNumber: string;
        email: string;
        code: string;
        amfCode: string;
        amfUsername: string;
        amfPassword: string;
        isActive: boolean;
        address: string;
        fiscTcr: string;
        fiscOperatorCode: string;
        bankAccount: string;
        uxhPerson: boolean;
        iosaUserId: number;
        accesses: Accesses;
        brokerId: number;
        brokerName: string;
        brokerCode: string;
        branchId: string;
        branch: any; // Update this to the appropriate interface if available
        policies: any[]; // Update this to the appropriate interface if available
        underwrittenPolicies: any[]; // Update this to the appropriate interface if available
        agencyProductCommissions: any[]; // Update this to the appropriate interface if available
        agencyProductTemplates: any[]; // Update this to the appropriate interface if available
        agencyRiskAddresses: any[]; // Update this to the appropriate interface if available
        configurations: any[]; // Update this to the appropriate interface if available
        transactions: any[]; // Update this to the appropriate interface if available
        id: string;
        domainEvents: any[]; // Update this to the appropriate interface if available
        isSystemEntity: boolean;
        createdOnUtc: string;
        updatedOnUtc: string | null;
        createdBy: string;
        updatedBy: string | null;
        iosaId: number;
    };
    mainAgency: {
        taxNumber: string;
        displayName: string;
        searchName: string;
        phoneNumber: string;
        email: string;
        code: string;
        amfCode: string;
        amfUsername: string;
        amfPassword: string;
        isActive: boolean;
        address: string;
        fiscTcr: string;
        fiscOperatorCode: string;
        bankAccount: string;
        uxhPerson: boolean;
        iosaUserId: number | null;
        accesses: Accesses | null;
        brokerId: number;
        brokerName: string;
        brokerCode: string;
        branchId: string;
        branch: any; // Update this to the appropriate interface if available
        policies: any[]; // Update this to the appropriate interface if available
        underwrittenPolicies: any[]; // Update this to the appropriate interface if available
        agencyProductCommissions: any[]; // Update this to the appropriate interface if available
        agencyProductTemplates: any[]; // Update this to the appropriate interface if available
        agencyRiskAddresses: any[]; // Update this to the appropriate interface if available
        configurations: any[]; // Update this to the appropriate interface if available
        transactions: any[]; // Update this to the appropriate interface if available
        id: string;
        domainEvents: any[]; // Update this to the appropriate interface if available
        isSystemEntity: boolean;
        createdOnUtc: string;
        updatedOnUtc: string | null;
        createdBy: string;
        updatedBy: string | null;
        iosaId: number;
    };
}

interface IAuthentication {
    user: User;
    agency: Agency;
}

class Authentication {
    private response: IAuthentication | undefined;

    // Called immediately after getting token from auth0 silently
    public async init() {
        const response: Result<IAuthentication | null> = await request(`/authentication`);

        if (response.data === null) return;

        this.response = response.data;
    }

    public getPermissions() {
        return this.response?.user.permissions ?? [];
    }

    public getAgencyModel() {
        return { ...this.response?.agency.agency, userId: this.response?.user.identityName, userFullName: this.response?.user.displayName };
    }

    public canIgnoreFailedInvoices() {
        return (
            this.response?.user.roles.includes('Administrator') ||
            this.response?.user.roles.includes('IT Employee') ||
            this.response?.user.roles.includes('Branch Manager') ||
            this.response?.user.roles.includes('Finance - Branch Specialist') ||
            this.response?.user.roles.includes('Finance - Headquarter Specialist') ||
            this.response?.user.roles.includes('Health - Sales Manager') ||
            this.response?.user.roles.includes('Motor - Sales Manager') ||
            this.response?.user.roles.includes('Property - Sales Manager')
        );
    }
}

export default new Authentication();
